import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { forceSignOutRedirect } from '../utils/force-sign-out';

// A Medmio API 401 means the session is dead (token expired / un-refreshable /
// revoked). Previously the SPA had no 401 handler: each call's catchError
// surfaced a per-page "Something went wrong" and the user was stranded in an
// authenticated shell with a dead session. This interceptor converts a 401
// into a clean forced sign-out + redirect to the sign-in screen, matching the
// manual logout in user-menu.component.ts.
@Injectable()
export class SessionExpiredInterceptor implements HttpInterceptor {
  // A dead session makes the whole screen's calls 401 at once (the home view
  // fires 6+ in parallel); only sign out once.
  private signingOut = false;

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Only react to 401s from the Medmio API. Cognito/AWS SDK calls and S3
    // asset fetches manage their own auth and must not force a logout.
    if (!request.url.startsWith(environment.apiUrlBase())) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          this.forceSignOut();
        }
        return throwError(() => err);
      }),
    );
  }

  private forceSignOut(): void {
    if (this.signingOut) return;
    // Don't loop if we're already on the sign-in surface.
    if (window.location.hash.startsWith('#/auth')) return;
    this.signingOut = true;
    void forceSignOutRedirect('expired');
  }
}
